import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import * as React from "react";
import { FC, useEffect } from "react";
import { connect } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    includeVatText = "includeVatText",
    excludeVatText = "excludeVatText"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.includeVatText]: string;
        [fields.excludeVatText]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    showVatValue: getCookie("ShowVat"),
    enableVat: getSettingsCollection(state).productSettings.enableVat,
});

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ThemeProps<BaseTheme>;

export interface ShowVatStyles {
    checkBoxProps?: IconPresentationProps;
    showVatGridItem?: GridItemProps;
    checkbox?: CheckboxPresentationProps;
}

export const showVatStyles: ShowVatStyles = {
    showVatGridItem: {
        width: 12,
        css: css`
            flex-direction: row;
            margin-bottom: .9375rem;
            padding-bottom: 0;
            margin-right: 1rem;
            text-transform: none;

            > div {
                flex-direction: row;
            }

            .section-heading {
                color: #535e64;
                font-size: .875rem;
                font-weight: bold;
            }

            @media screen and (max-width: 990px) {
                flex-direction: column;

                span.section-heading {
                    + div {
                        margin-bottom: 1.125rem;
                    }
                }
            }
        `,
    },
    checkbox: {
        css: css`
            margin-left: 0.5rem;
            margin-right: 0.5rem;

            > div {
                margin: 0 !important;
            }
       `,
    },

    checkBoxProps: {

    },
};

const styles = showVatStyles;

export const ShowVat: FC<Props> = ({
    fields,
    showVatValue,
    enableVat,
}) => {

    const [checked, setChecked] = React.useState(false);

    useEffect(() => {
        if (showVatValue === undefined) {
            showVatValue = "false";
            setCookie("ShowVat", showVatValue);
        } else {
            setChecked((showVatValue === "true"))
        }
    }, []);

    const handleShowVatFlagChanged = (_: React.SyntheticEvent, value: boolean) => {
        setChecked(value);
        setCookie("ShowVat", value.toString());
        window.location.reload();
    };

    return ( 
        <>
            {enableVat && (
                <GridItem {...styles.showVatGridItem}>
                    <span className="section-heading">{fields.excludeVatText}</span>
                    <CheckboxGroup>
                        <Checkbox
                                uid="showVatCheckbox"
                                checked={checked}
                                variant="toggle"
                                onChange={handleShowVatFlagChanged}
                                {...styles.checkbox}
                            >
                        </Checkbox>
                    </CheckboxGroup>
                    <span className="section-heading">{fields.includeVatText}</span>
            </GridItem>
            )}
        </>
    );
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withTheme(ShowVat)),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.includeVatText,
                displayName: "Include VAT Text",
                tooltip: "Text for including VAT",
                editorTemplate: "TextField",
                defaultValue: "Include VAT",
                fieldType: "Translatable",
            },
            {
                name: fields.excludeVatText,
                displayName: "Exclude VAT Text",
                tooltip: "Text for excluding VAT",
                editorTemplate: "TextField",
                defaultValue: "Exclude VAT",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
